import { stripEditsBlock } from '@/lib/edits';
import { renderMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';
import { useApp, type ChatMessage } from '@/store/useApp';

interface MessageBubbleProps {
  message: ChatMessage;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  // Hide the raw <edits> XML payload from the chat — show only Claude's summary.
  // Strips both complete blocks and partial open-only ones (mid-stream).
  const displayContent =
    !isUser && !isSystem ? stripEditsBlock(message.content) : message.content;
  const showMarkdown = !isUser && !isSystem && Boolean(displayContent);
  const isStreaming = message.status === 'streaming';
  const streamProgress = useApp((s) => (isStreaming ? s.streamProgress : null));

  return (
    <div
      className={cn(
        'flex w-full',
        isUser ? 'justify-end' : 'justify-start',
      )}
    >
      <div
        className={cn(
          'max-w-[85%] break-words rounded-2xl px-3.5 py-2 text-sm leading-relaxed',
          isUser && 'whitespace-pre-wrap bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900',
          !isUser && !isSystem && 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-100',
          isSystem &&
            'whitespace-pre-wrap border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200',
        )}
      >
        {showMarkdown ? (
          <div
            className="cdc-md"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(displayContent) }}
          />
        ) : displayContent ? (
          displayContent
        ) : isStreaming ? (
          <StreamingPlaceholder progress={streamProgress} />
        ) : null}
        {message.attachments && message.attachments.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {message.attachments.map((a, i) => {
              if (a.kind === 'image') {
                return (
                  <li
                    key={i}
                    className="h-12 w-12 overflow-hidden rounded border border-white/20 dark:border-black/20"
                  >
                    <img
                      src={a.dataUrl}
                      alt="attachment"
                      className="h-full w-full object-cover"
                    />
                  </li>
                );
              }
              return (
                <li
                  key={i}
                  className="flex max-w-[12rem] items-center gap-1.5 rounded border border-white/20 bg-white/10 px-2 py-1 text-[11px] dark:border-black/20 dark:bg-black/20"
                  title={a.name}
                >
                  <span aria-hidden="true">{a.kind === 'document' ? '📄' : '📎'}</span>
                  <span className="truncate">{a.name}</span>
                </li>
              );
            })}
          </ul>
        )}
        {message.status === 'cancelled' && (
          <p className="mt-1 text-[11px] uppercase tracking-wider opacity-70">cancelled</p>
        )}
        {message.status === 'partial' && (
          <p className="mt-1 text-[11px] uppercase tracking-wider opacity-70">partial</p>
        )}
        {message.status === 'error' && (
          <p className="mt-1 text-[11px] uppercase tracking-wider opacity-70">error</p>
        )}
      </div>
    </div>
  );
}

function StreamingPlaceholder({
  progress,
}: {
  progress: { startedAt: number; totalBytes: number } | null;
}) {
  return (
    <div className="flex items-center gap-2 text-neutral-400">
      <span className="inline-flex gap-0.5">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" style={{ animationDelay: '0ms' }} />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" style={{ animationDelay: '150ms' }} />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current" style={{ animationDelay: '300ms' }} />
      </span>
      <span className="text-[11px]">
        {progress && progress.totalBytes > 0
          ? `thinking — received ${(progress.totalBytes / 1024).toFixed(1)} KB so far`
          : 'thinking — large prompts may take a moment to start'}
      </span>
    </div>
  );
}
